import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Loader2, CheckCircle2, Pencil } from "lucide-react";

const PREOCC = [
  { v: "invalidita", l: "Restare invalido e non poter lavorare" },
  { v: "famiglia", l: "Lasciare la famiglia senza sostegno" },
  { v: "ricovero", l: "Giorni di ricovero o gesso" },
  { v: "spese", l: "Spese mediche dopo un infortunio" },
];

export default function ReteSolidale() {
  const [sp] = useSearchParams();
  const [conv, setConv] = useState(null);
  const [codice, setCodice] = useState((sp.get("c") || "").toUpperCase());
  const [editCode, setEditCode] = useState(false);
  const [prodotto, setProdotto] = useState(sp.get("p") || "infortuni");
  const [f, setF] = useState({ nome: "", cognome: "", email: "", telefono: "", comune: "", anno_nascita: "",
    come_parte: "", ente: "", ruolo: "", professione: "", ambito: "h24", attivita_rischio: "", note: "",
    contatto_pref: "telefono", orario: "mattina" });
  const [preocc, setPreocc] = useState([]);
  const [cAppart, setCAppart] = useState(false);
  const [cPrivacy, setCPrivacy] = useState(false);
  const [cMkt, setCMkt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const loadConv = (code) => api.get(`/public/rete-solidale/${code || "-"}`).then(({ data }) => setConv(data)).catch(() => setConv(null));
  useEffect(() => {
    loadConv(codice);
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []); // eslint-disable-line

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const togglePreocc = (v) => setPreocc((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);
  const prodotti = conv?.prodotti || [{ value: "infortuni", label: "Infortuni", attivo: true }];
  const categorie = conv?.categorie || [];

  const submit = async () => {
    if (!cPrivacy) return toast.error("È necessario il consenso privacy");
    setBusy(true);
    try {
      const { data } = await api.post("/public/rete-solidale", {
        codice, prodotto, ...f, preoccupazioni: preocc, operatore: sp.get("o") || "",
        consenso_appartenenza: cAppart, consenso_privacy: cPrivacy, consenso_marketing: cMkt,
      });
      setDone(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone p-6">
        <Card className="max-w-md rounded-2xl border-border/60 p-8 text-center" data-testid="rs-done">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-600" />
          <h1 className="font-heading text-2xl font-semibold text-forest">Richiesta inviata</h1>
          <p className="mt-2 text-sm text-muted-foreground">{done.message}</p>
          <p className="mt-3 text-xs text-muted-foreground">Riferimento pratica: <b>{done.codice_pratica}</b></p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone px-4 py-8" data-testid="rete-solidale-page">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <div className="flex items-center gap-2 text-terracotta">
          <ShieldCheck className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Rete Solidale Assietica</span>
        </div>
        <h1 className="font-heading text-4xl font-semibold leading-tight text-forest">Protezione su misura per chi fa rete</h1>
        <p className="text-base text-muted-foreground">Riservato a soci e lavoratori di cooperative, volontari degli enti del Terzo Settore e familiari delle associazioni aderenti. Raccontaci cosa ti serve: ricevi una proposta personale, preparata da un consulente.</p>

        <Card className="flex items-center justify-between rounded-xl border-border/60 bg-sage/30 p-4" data-testid="rs-codice">
          <div className="text-sm">
            <span className="text-muted-foreground">Codice convenzione </span>
            {editCode ? (
              <Input value={codice} onChange={(e) => setCodice(e.target.value.toUpperCase())} onBlur={() => { setEditCode(false); loadConv(codice); }}
                className="ml-2 inline-block h-8 w-48" data-testid="rs-codice-input" autoFocus />
            ) : (
              <><b className="text-forest">{codice || "—"}</b>{conv?.ente_promotore && <span className="text-muted-foreground"> · {conv.ente_promotore}</span>}
              {conv && !conv.valida && codice && <span className="ml-2 text-xs text-amber-700">(non attivo/scaduto: verrai comunque ricontattato)</span>}</>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditCode(true)} data-testid="rs-codice-edit" className="gap-1.5 border-forest/30 text-forest"><Pencil className="h-3.5 w-3.5" /> Modifica</Button>
        </Card>

        {/* 1. Cosa ti serve */}
        <Card className="rounded-xl border-border/60 p-5">
          <h2 className="mb-3 font-heading text-lg font-semibold text-forest"><span className="text-terracotta">1</span> Cosa ti serve</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {["infortuni", "casa", "auto"].map((pv) => {
              const meta = prodotti.find((x) => x.value === pv) || { value: pv, label: pv, attivo: false };
              const attivo = meta.attivo;
              const desc = pv === "infortuni" ? "Tutela nel lavoro, nel volontariato e nel tempo libero." : pv === "casa" ? "Abitazione, contenuto, responsabilità civile della famiglia." : "RC auto: inserisci la targa sul sito Assietica.";
              if (pv === "auto") {
                return (
                  <a key={pv} href="https://assietica.it/basta-la-targa" target="_blank" rel="noopener noreferrer"
                    data-testid="rs-prod-auto" className="rounded-xl border-2 border-border/60 p-4 text-left transition-all hover:border-forest/40">
                    <div className="font-heading font-semibold text-forest">{meta.label || "Auto (RCA)"}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
                    <div className="mt-1 text-[11px] font-semibold uppercase text-terracotta">Basta la Targa →</div>
                  </a>
                );
              }
              return (
                <button key={pv} data-testid={`rs-prod-${pv}`} disabled={!attivo} onClick={() => attivo && setProdotto(pv)}
                  className={`rounded-xl border-2 p-4 text-left transition-all ${prodotto === pv && attivo ? "border-forest bg-sage/30" : "border-border/60"} ${attivo ? "hover:border-forest/40" : "opacity-70"}`}>
                  <div className="font-heading font-semibold text-forest">{meta.label}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
                  {!attivo && <div className="mt-1 text-[11px] font-semibold uppercase text-terracotta">In arrivo</div>}
                </button>
              );
            })}
          </div>
        </Card>

        {/* 2. Chi sei */}
        <Card className="rounded-xl border-border/60 p-5">
          <h2 className="mb-1 font-heading text-lg font-semibold text-forest"><span className="text-terracotta">2</span> Chi sei</h2>
          <p className="mb-3 text-sm text-muted-foreground">Ci servono per contattarti e per verificare la tua appartenenza alla rete.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="rs-nome" value={f.nome} onChange={set("nome")} /></div>
            <div className="space-y-1.5"><Label>Cognome</Label><Input data-testid="rs-cognome" value={f.cognome} onChange={set("cognome")} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="rs-email" value={f.email} onChange={set("email")} /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input data-testid="rs-telefono" value={f.telefono} onChange={set("telefono")} /></div>
            <div className="space-y-1.5"><Label>Comune di residenza</Label><Input value={f.comune} onChange={set("comune")} /></div>
            <div className="space-y-1.5"><Label>Anno di nascita</Label><Input placeholder="es. 1978" value={f.anno_nascita} onChange={set("anno_nascita")} /></div>
            <div className="space-y-1.5">
              <Label>Come fai parte della rete</Label>
              <Select value={f.come_parte} onValueChange={(v) => setF({ ...f, come_parte: v })}>
                <SelectTrigger data-testid="rs-come-parte"><SelectValue placeholder="Scegli…" /></SelectTrigger>
                <SelectContent>{(categorie.length ? categorie : []).map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Ente o cooperativa</Label><Input placeholder="Nome dell'ente" value={f.ente} onChange={set("ente")} /></div>
          </div>
          <div className="mt-3 space-y-1.5"><Label>Il tuo ruolo nell'ente <span className="text-muted-foreground">(facoltativo)</span></Label>
            <Input placeholder="es. volontario trasporti, socio lavoratore, tesoriere" value={f.ruolo} onChange={set("ruolo")} /></div>
        </Card>

        {/* 3. Protezione infortuni */}
        {prodotto === "infortuni" && (
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-1 font-heading text-lg font-semibold text-forest"><span className="text-terracotta">3</span> La tua protezione infortuni</h2>
            <p className="mb-3 text-sm text-muted-foreground">Bastano poche informazioni: il resto lo vediamo insieme.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Professione</Label><Input placeholder="es. educatore, impiegata, pensionato" value={f.professione} onChange={set("professione")} /></div>
              <div className="space-y-1.5"><Label>Quando vuoi essere coperto</Label>
                <Select value={f.ambito} onValueChange={(v) => setF({ ...f, ambito: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="h24">Sempre, 24 ore su 24</SelectItem><SelectItem value="professionale">Attività professionale</SelectItem><SelectItem value="extra">Tempo libero</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3"><Label>Cosa ti preoccupa di più</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {PREOCC.map((p) => (
                  <label key={p.v} className="flex cursor-pointer items-center gap-2 text-sm text-forest">
                    <Checkbox checked={preocc.includes(p.v)} onCheckedChange={() => togglePreocc(p.v)} data-testid={`rs-preocc-${p.v}`} /> {p.l}
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-3 space-y-1.5"><Label>Attività sportiva o di volontariato a rischio <span className="text-muted-foreground">(facoltativo)</span></Label>
              <Input placeholder="es. protezione civile, trasporto disabili, ciclismo" value={f.attivita_rischio} onChange={set("attivita_rischio")} /></div>
          </Card>
        )}

        {/* 4. Note e consensi */}
        <Card className="rounded-xl border-border/60 p-5">
          <h2 className="mb-3 font-heading text-lg font-semibold text-forest"><span className="text-terracotta">4</span> Note e consensi</h2>
          <div className="space-y-1.5"><Label>Qualcosa da aggiungere <span className="text-muted-foreground">(facoltativo)</span></Label>
            <Textarea placeholder="es. ho già una polizza in scadenza a marzo" value={f.note} onChange={set("note")} /></div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Preferisci essere contattato</Label>
              <Select value={f.contatto_pref} onValueChange={(v) => setF({ ...f, contatto_pref: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="telefono">Per telefono</SelectItem><SelectItem value="email">Per email</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Orario migliore</Label>
              <Select value={f.orario} onValueChange={(v) => setF({ ...f, orario: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mattina">Mattina</SelectItem><SelectItem value="pomeriggio">Pomeriggio</SelectItem><SelectItem value="sera">Sera</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="mt-4 space-y-2.5 text-sm">
            <label className="flex cursor-pointer items-start gap-2"><Checkbox checked={cAppart} onCheckedChange={setCAppart} data-testid="rs-c-appart" /><span><b>Dichiaro</b> di appartenere all'ente indicato con il ruolo dichiarato e mi impegno a fornirne prova prima della sottoscrizione.</span></label>
            <label className="flex cursor-pointer items-start gap-2"><Checkbox checked={cPrivacy} onCheckedChange={setCPrivacy} data-testid="rs-c-privacy" /><span><b>Ho letto l'informativa privacy</b> e acconsento al trattamento dei dati per ricevere la proposta richiesta.</span></label>
            <label className="flex cursor-pointer items-start gap-2"><Checkbox checked={cMkt} onCheckedChange={setCMkt} data-testid="rs-c-mkt" /><span>Voglio ricevere aggiornamenti su iniziative della Rete Solidale <span className="text-muted-foreground">(facoltativo)</span>.</span></label>
          </div>
        </Card>

        <div className="flex flex-wrap items-center gap-4 pb-8">
          <Button onClick={submit} disabled={busy} data-testid="rs-submit" className="gap-2 rounded-full bg-terracotta px-6 py-6 text-base hover:bg-terracotta/90">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Chiedi la tua proposta
          </Button>
          <span className="text-sm text-muted-foreground">Nessun impegno: ricevi un'offerta personale, valida 30 giorni.</span>
        </div>
      </div>
    </div>
  );
}
